/*
 * Top level module for Reports page
 */

//DataModel imported because we reqres it
define([
  "marionette",
  "backbone",
  "highcharts/highcharts",
  "jquery",
  "messaging/messenger",
  "modules/reportSplit/reports/reportArea/reportAreaView",
  "modules/reportSplit/reports/reportArea/dataModel",
  "modules/reportSplit/reports/configArea/configAreaModule",
  "modules/shared/sortableList/sortableListModule",
  "modules/shared/sortableList/sortableListModel",
  "modules/reportSplit/reports/singleReport/singleReportModule",
  "modules/reportSplit/reports/singleReport/reportUtilities",
  "modules/reportSplit/reports/reportArea/oneBigReportView",
], function (
  Marionette,
  Backbone,
  Highcharts,
  $,
  Messenger,
  ReportAreaView,
  DataModel,
  ConfigAreaModule,
  SortableListModule,
  SortableListModel,
  SingleReportModule,
  ReportUtilities,
  OneBigReportView,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "reportAreaModule";
      this.isOpen = false;
      this.region = options.region;
      this._setUpVents();
    },
    /**
     * This function fires when the report area is first shown.  Its main function is to show the layout and update the
     * URL/history. Currently it also displays a dummy report just to show that it's visible.  Eventually we'll either
     * get rid of that, or have a dashboard show up by default.
     * @returns {nothing}
     */
    show: function (currentReport) {
      //don't navigate here.  /reports is handled higher up
      //Backbone.history.navigate(kbId + "/reports");
      if (currentReport.layout === undefined || currentReport.layout === null) {
        this.layout = new ReportAreaView.reportsLayout();
      } else if (currentReport.layout === "oneBigReport") {
        this.layout = new OneBigReportView.reportsLayout();
      }
    },
    hideReport: function (report) {
      this.layout.hideReportArea(report.displayHint);
    },
    /**
     * This is the primary function of this module.  It handles the display of the reports.  When a new report is
     * added, we'll add code here, even if that code is only to hand off to another module to do the heavy lifting
     * (which currently is almost all done here). For now, our HighCharts code lives here.
     *
     * Side effect: a report shows up
     *
     * @param report (R) - The report object that contains the report's metadata (href to retrieve data,
     *     parameters, etc.). This is usually send either by clicking on it in a list, or as a drilldown from another
     *     report.

     * @returns {nothing}
     */
    _displayReport: function (report) {
      if (report.drillDownLevel <= 0) {
        //check if report looks okay
        const regex = /\{.+?}/g;
        //for some reason we have to do string.match(regex) to get both parameters.
        //regex.exec(string) only gives the first match, repeated as many times as it happens
        const paramArray = report.href.match(regex);
        let paramCount = 0;
        if (paramArray !== null) {
          //if it is not null, there is at least one parameter.  otherwise, use the default of zero
          paramCount = paramArray.length;
        }
        if (paramCount > report.reportArguments.length) {
          Messenger.okDialog(
            "Report " +
              report.name +
              " has " +
              paramCount +
              " parameters in its href, but " +
              report.reportArguments.length +
              " parameters were received by the client.  Did you forget to add some/all parameters to the report?",
          );
        } else if (paramCount < report.reportArguments.length) {
          Messenger.okDialog(
            "Report " +
              report.name +
              " has " +
              paramCount +
              " parameters in its href, but " +
              report.reportArguments.length +
              " parameters were received by the client.  Did you forget to update the HREF?",
          );
        }
      }

      //show it
      this.isOpen = true;
      //Show the necessary report areas before displaying the report.
      if (report.displayHint) {
        this.layout.showReportArea(report.displayHint);
      }

      //see if we can spin this off into a separate module
      //Figure out which Marionette region to display in

      const layout = this.layout;
      let region = layout.chart;
      if (report.displayHint === "center") {
        region = layout.chart;
      }
      if (report.displayHint === "west") {
        region = layout.westChart;
      }
      if (report.displayHint === "east") {
        region = layout.eastChart;
      }
      if (report.displayHint === "southWest") {
        region = layout.southWestChart;
      }
      if (report.displayHint === "southEast") {
        region = layout.southEastChart;
      }
      if (report.displayHint === "south") {
        region = layout.southChart;
      }

      this.newReport = this.addSubModule(new SingleReportModule({ region: region }));
      this.newReport.showIndividualReport(report);
    },
    /**
     * _setUpVents configures the messaging.
     */
    _setUpVents: function () {
      /**
       * Perhaps the most important vent for this module is show:report.
       * The first parameter is report, and contains a reportModel.  It is also the only strictly required parameter.
       * leaveExistingConfigView should generally be true if this is reached by a means other than the list of reports.
       * dontSkipReport needs refactoring.
       */
      Messenger.vent.on(
        "show:drilldown",
        function (report) {
          if (report.toJSON) {
            report = report.toJSON();
          }

          console.log("show report");

          const kbId = Messenger.reqres.request("active:kb");

          let url = report.href.substring(1);
          url = ReportUtilities.figureOutText(url);
          if (!report.fromHistory) {
            Backbone.history.navigate(kbId + "/reports/" + url);
          }
          /**
           * Set up sub-vents.  The first one lets drilldowns figure out where to call to get data.
           * The second one simply returns the current report.
           * This also needs some refactoring (of names).
           */
          Messenger.reqres.setHandler("currentReportUrl", function () {
            return report.href;
          });
          let reports = Messenger.reqres.request("currentReports");
          let found = false;
          for (let i = 0; i < reports.length; i++) {
            const iterReport = reports[i];
            if (iterReport.name === report.name) {
              reports[i] = report;
              found = true;
            }
            if (iterReport.drillDownLevel > report.drillDownLevel) {
              this.hideReport(reports[i]);
              reports = reports.slice(i, i + 1);
              break;
            }
          }
          if (!found) {
            reports.push(report);
          }
          Messenger.reqres.setHandler("currentReports", function () {
            return reports;
          });
          /**
           * Configuration is now done, so shwo the report.
           */
          this._displayReport(report);
        },
        this,
      );

      /**
       * Another important specialty vent.  This is called when configuration options have changed, and redraws the
       * current report with those updated configuration options.
       * @param {report} report The report to be updated
       */
      Messenger.vent.on(
        "update:report",
        function (report) {
          /**
           * History
           */
          let rpts;
          if (report.toJSON) {
            rpts = report.toJSON().componentReports;
          } else {
            rpts = report.componentReports;
          }

          //see if it's just a plain-old report, not a dashboard
          if (rpts.length === 0) {
            rpts.push(report);
          }
          /**
           * History
           */

          //Special case - ensure there are at most 7 days for Topic Detail Report if Daily selected
          //The stored proc would need to be modified to support more.
          //It would also get very tough to read with many days
          //I wouldn't be opposed to having a bit more than 7, but that isn't supported now
          if (report.href.indexOf("topicDetail") > -1) {
            const interval = Messenger.reqres.request("param:{interval}", "{interval}", 2);
            if (interval === "Daily") {
              const startDate = Messenger.reqres.request("param:{startDate}", "{startDate}", 0);
              const endDate = Messenger.reqres.request("param:{endDate}", "{endDate}", 1);
              //calculate how far between 'em
              const startMillis = Date.parse(startDate);
              const endMillis = Date.parse(endDate);
              //518400000 is 6 days in milliseconds.  We want 6, not 7, because we include both the first and last days
              // requested
              if (endMillis - startMillis > 518400000) {
                Messenger.okDialog(
                  "With a Daily interval, the start and end date must span at most 7 days",
                );
                return;
              }
            }
          }

          //backbone history
          const kbId = Messenger.reqres.request("active:kb");
          //get the parameters
          let url = report.href.substring(1);
          //var url = rpts[0].href.substring(1);
          const regex_double_slash = /\/\//g; //two /'s in a row
          const regex_slash_with_question_mark = /\/\?/g; // to match pattern /?query=value
          url = ReportUtilities.figureOutText(url);
          //If the last character is blank, the last parameter was omitted.
          //Ideally we'd use string.endsWith, but only Firefox (17+) supports this currently
          if (
            url.charAt(url.length - 1) === "/" ||
            url.match(regex_double_slash) !== null ||
            url.match(regex_slash_with_question_mark) !== null
          ) {
            Messenger.okDialog("reports.errors.notAllParametersSelected");
            return;
          }
          //Only navigate if the report is new (hit Apply, not back button), or there is no drilldown
          if (
            report.fromHistory === false ||
            report.fromHistory === undefined ||
            report.drillDownReport === null
          ) {
            //Also make sure this isn't auto-displaying with the default parameters (which should leave the default URL)
            if (!report.autoDisplay) {
              if (url.includes("?")) {
                let [path, queryString] = url.split("?");
                let queryParams = new URLSearchParams(queryString);
                let pathParams = [];
                queryParams.forEach((value, key) => {
                  pathParams.push(`${key}=${value}`);
                });
                url = `${path}/${pathParams.join(";")}`;
              }
              Backbone.history.navigate(kbId + "/reports/" + url);
            }
          }

          //anjdjon 6/27/14 Hide drilldown reports when changing top-level config (similar to how we hide the
          //escalation detail report if we change the first-level drilldown on that dashboard)
          const reports = Messenger.reqres.request("currentReports");
          let found = false;
          for (let i = 0; i < reports.length; i++) {
            const iterReport = reports[i];
            if (iterReport.name === report.name) {
              reports[i] = report;
              found = true;
            }
            if (iterReport.drillDownLevel > report.drillDownLevel) {
              this.hideReport(reports[i]);
              reports.splice(i, i + 1);
              i--; //don't skip the next one in the loop
            }
          }

          //before we start calling _display report to add the single report submodules, we need
          //to make sure we close any existing single report submodules, otherwise we will just end
          //up with an ever increasing number of submodules.  So if the reports are already loaded, close the
          //layout to close all of the submodules and then re-show the layout.
          if (this.isOpen) {
            this.layout.close();
          }
          this.showRegion(this.layout);

          for (let i = 0; i < rpts.length; i++) {
            const rpt = rpts[i];
            if (!rpt.isDashboard) {
              this._displayReport(rpt, true);
            } else {
              for (let c = 0; c < rpt.componentReports.length; c++) {
                const subReport = rpt.componentReports[c];
                this._displayReport(subReport, true);
              }
            }
          }
        },
        this,
      );

      Messenger.reqres.setHandler(
        "report:area:highcharts:data",
        function (area) {
          return this.layout.getHighcharts(area);
        },
        this,
      );
      //We intentionally don't have a save vent here since there's nothing to save and this isn't a top-level module
    },
  });
});
